import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GetAllSponsorActivitiesAPI } from "@/services/api/sponsor/getAllSponsorActivities";
import { getActivityStatus, isTaskCompleted } from "@/utils/sponsorStatus";
import Icon from "@/components/Icon";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminStatusPill from "@/components/AdminStatusPill";
import Modal from "@/components/Modal";

type FilterKey = "all" | "pending" | "completed" | "failed";

/**
 * Admin sponsor activities list: filterable by status, with infinite scroll.
 * Clicking a card opens the sponsor task check screen.
 */
const SponsorsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [filterVisible, setFilterVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const fetchActivities = async (page: number = 1, append: boolean = false) => {
    if (page === 1) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    const resp = await GetAllSponsorActivitiesAPI(page);
    if (resp.status === 0 && Array.isArray(resp.resp)) {
      if (append) {
        setActivities((prev) => [...prev, ...resp.resp]);
      } else {
        setActivities(resp.resp);
      }
      setCurrentPage(page);
      setHasMore(resp.resp.length >= (resp.limit || 20));
    } else {
      if (!append) {
        setError(typeof resp.resp === "string" ? resp.resp : t("Failed to fetch sponsor activities"));
      }
    }
    setLoading(false);
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      fetchActivities(currentPage + 1, true);
    }
  }, [currentPage, loadingMore, hasMore, loading]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const getTaskProgress = (completions: any[]) => {
    if (!completions || completions.length === 0) return { completed: 0, total: 0 };
    const completed = completions.filter((c) => isTaskCompleted(c)).length;
    return { completed, total: completions.length };
  };

  const filteredActivities = activities.filter((item) => {
    if (activeFilter === "all") return true;
    const { status } = getActivityStatus(item.completions || [], item.activity_date);
    return status === activeFilter;
  });

  const filterOptions: { key: FilterKey; label: string; count: number }[] = [
    { key: "all", label: t("All"), count: activities.length },
    {
      key: "pending",
      label: t("Pending"),
      count: activities.filter(
        (a) => getActivityStatus(a.completions || [], a.activity_date).status === "pending",
      ).length,
    },
    {
      key: "completed",
      label: t("Completed"),
      count: activities.filter(
        (a) => getActivityStatus(a.completions || [], a.activity_date).status === "completed",
      ).length,
    },
    {
      key: "failed",
      label: t("Failed"),
      count: activities.filter(
        (a) => getActivityStatus(a.completions || [], a.activity_date).status === "failed",
      ).length,
    },
  ];

  const renderCompensationBadge = (item: any) => {
    const completions = item.completions || [];
    const progress = getTaskProgress(completions);
    const activityStatus = getActivityStatus(completions, item.activity_date);
    if (activityStatus.status !== "failed") return null;

    const allCompleted = progress.total > 0 && progress.completed === progress.total;
    const hasCompensation = !!item.compensation_amount;
    const hasReceipt = !!item.compensation_receipt_url;
    const approvalStatus = item.compensation_approval_status;

    let label = "";
    let bgColor = "#fff3e0";
    let textColor = "#e65100";
    let iconName = "alert-circle-outline";

    if (allCompleted) {
      label = t("No Compensation");
      bgColor = "#e8f5e9";
      textColor = "#2e7d32";
      iconName = "check-circle-outline";
    } else if (hasReceipt && approvalStatus === "approved") {
      label = t("Receipt Approved");
      bgColor = "#e8f5e9";
      textColor = "#2e7d32";
      iconName = "check-circle";
    } else if (hasReceipt && approvalStatus === "rejected") {
      label = t("Receipt Rejected");
      bgColor = "#ffebee";
      textColor = "#c62828";
      iconName = "close-circle";
    } else if (hasReceipt && approvalStatus === "pending") {
      label = t("Receipt Pending");
      bgColor = "#fff3e0";
      textColor = "#e65100";
      iconName = "clock-outline";
    } else if (hasCompensation) {
      label = t("Compensation: HKD ${{amount}}", {
        amount: Number(item.compensation_amount).toLocaleString(),
      });
      bgColor = "#ffebee";
      textColor = "#c62828";
      iconName = "alert-circle";
    } else {
      label = t("Compensation Required");
      bgColor = "#fff3e0";
      textColor = "#e65100";
      iconName = "alert-circle-outline";
    }

    return (
      <div
        className="mt-2 flex items-center gap-1 self-start rounded-lg px-2.5 py-1.5"
        style={{ backgroundColor: bgColor }}
      >
        <Icon iconType="MaterialCommunityIcons" name={iconName} size={14} color={textColor} />
        <span className="text-xs font-bold" style={{ color: textColor }}>
          {label}
        </span>
      </div>
    );
  };

  return (
    <div className="flex min-h-full flex-col p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#222]">{t("Sponsor Activities")}</h1>
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#e0e0e0] bg-white transition hover:bg-gray-50"
          onClick={() => setFilterVisible(true)}
        >
          <Icon iconType="AntDesign" name="filter" size={20} color="#ffa217" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto pb-4">
        {loading ? (
          <AdminListSkeleton count={4} />
        ) : error ? (
          <AdminEmptyState
            iconType="MaterialCommunityIcons"
            iconName="cloud-alert-outline"
            title={t("Something went wrong")}
            message={error}
            actionLabel={t("Retry")}
            onAction={() => fetchActivities(1, false)}
          />
        ) : filteredActivities.length === 0 ? (
          <AdminEmptyState
            iconType="MaterialCommunityIcons"
            iconName="hand-coin-outline"
            title={t("No sponsor activities to review")}
            message={t("Pull to refresh to load sponsor activities.")}
            actionLabel={t("Refresh")}
            onAction={() => fetchActivities(1, false)}
          />
        ) : (
          <>
            <div className="w-full">
              {filteredActivities.map((item) => {
                const student = item.student || {};
                const sponsor = item.sponsor || {};
                const society = item.society || {};
                const completions = item.completions || [];
                const progress = getTaskProgress(completions);
                const activityStatus = getActivityStatus(completions, item.activity_date);
                const isSociety = item.type === "society";
                const hasAgreement = !!item.signed_agreement;

                return (
                  <button
                    key={item.id}
                    type="button"
                    className="mb-3 w-full rounded-xl bg-white p-4 text-left shadow-sm transition hover:shadow"
                    onClick={() => navigate(`/console/sponsors/${item.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex min-w-0 flex-1 items-center">
                        {sponsor.image_url ? (
                          <img
                            src={sponsor.image_url}
                            alt=""
                            className="mr-3 h-11 w-11 rounded-full object-cover"
                          />
                        ) : (
                          <div className="mr-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#F0F6FF]">
                            <span className="text-lg text-[#bbb]">
                              {sponsor.name?.[0] || "?"}
                            </span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-base font-semibold text-[#222]">
                            {item.activity_name || "-"}
                          </div>
                          <div className="mt-0.5 truncate text-sm text-[#ffa217]">
                            {sponsor.name || "-"}
                          </div>
                          <div className="mt-0.5 truncate text-[13px] text-[#666]">
                            {isSociety ? society.name || "-" : student.username || "-"}
                          </div>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span
                          className="rounded-[10px] px-2 py-0.5 text-[11px] font-bold text-white"
                          style={{ backgroundColor: isSociety ? "#7c4dff" : "#2196f3" }}
                        >
                          {isSociety ? t("Society") : t("Personal")}
                        </span>
                        <AdminStatusPill
                          label={
                            activityStatus.status === "completed"
                              ? t("Completed")
                              : activityStatus.status === "failed"
                                ? t("Failed")
                                : t("Pending")
                          }
                          tone={
                            activityStatus.status === "completed"
                              ? "success"
                              : activityStatus.status === "failed"
                                ? "danger"
                                : "warning"
                          }
                          iconType="MaterialCommunityIcons"
                          iconName={
                            activityStatus.status === "completed"
                              ? "check-circle"
                              : activityStatus.status === "failed"
                                ? "close-circle"
                                : "clock-outline"
                          }
                          iconSize={12}
                        />
                        <span
                          className="rounded-[10px] px-2 py-0.5 text-[11px] font-bold text-white"
                          style={{
                            backgroundColor:
                              progress.completed === progress.total && progress.total > 0
                                ? "#4caf50"
                                : "#ff9800",
                          }}
                        >
                          {`${progress.completed}/${progress.total}`}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2.5 flex flex-wrap gap-3">
                      {item.activity_date && (
                        <div className="flex items-center gap-1">
                          <Icon iconType="MaterialCommunityIcons" name="calendar" size={14} color="#888" />
                          <span className="text-xs text-[#888]">{item.activity_date}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Icon
                          iconType="MaterialCommunityIcons"
                          name="file-document-outline"
                          size={14}
                          color={hasAgreement ? "#4caf50" : "#ccc"}
                        />
                        <span
                          className="text-xs"
                          style={{ color: hasAgreement ? "#4caf50" : "#ccc" }}
                        >
                          {hasAgreement ? t("Agreed") : t("No Agreement")}
                        </span>
                      </div>
                    </div>

                    {renderCompensationBadge(item)}

                    <div className="mt-2.5 flex items-center justify-end gap-1">
                      <span className="text-[13px] font-medium text-[#ffa217]">
                        {t("View Details")}
                      </span>
                      <Icon iconType="MaterialCommunityIcons" name="chevron-right" size={18} color="#ffa217" />
                    </div>
                  </button>
                );
              })}
            </div>

            {loadingMore && <AdminListSkeleton count={2} />}
          </>
        )}
      </div>

      {/* Filter modal */}
      <Modal visible={filterVisible} onClose={() => setFilterVisible(false)} position="bottom">
        <div className="rounded-t-[20px] bg-white p-5 pb-9 shadow-lg">
          <div className="mb-4 flex items-center justify-between">
            <div className="text-lg font-bold text-[#222]">{t("Filter")}</div>
            <button type="button" onClick={() => setFilterVisible(false)}>
              <Icon iconType="MaterialCommunityIcons" name="close" size={24} color="#000" />
            </button>
          </div>
          {filterOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`mb-2 flex w-full items-center justify-between rounded-xl border px-4 py-3.5 transition ${
                activeFilter === option.key
                  ? "border-[#ffa217] bg-[#ffa217]"
                  : "border-[#e8e8e8] bg-[#f5f5f5]"
              }`}
              onClick={() => {
                setActiveFilter(option.key);
                setFilterVisible(false);
              }}
            >
              <div className="flex items-center gap-2.5">
                <Icon
                  iconType="MaterialCommunityIcons"
                  name={
                    option.key === "all"
                      ? "view-grid-outline"
                      : option.key === "pending"
                        ? "clock-outline"
                        : option.key === "completed"
                          ? "check-circle-outline"
                          : "close-circle-outline"
                  }
                  size={18}
                  color={activeFilter === option.key ? "#fff" : "#888"}
                />
                <span
                  className={`text-[15px] ${
                    activeFilter === option.key ? "font-bold text-white" : "font-medium text-[#444]"
                  }`}
                >
                  {option.label}
                </span>
              </div>
              <span
                className={`min-w-[28px] rounded-full px-2 py-0.5 text-center text-[13px] ${
                  activeFilter === option.key
                    ? "bg-white/25 text-white"
                    : "bg-white text-[#666]"
                }`}
              >
                {option.count}
              </span>
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default SponsorsPage;